import { useEffect, useMemo, useRef, useState } from 'react';
import { highlightCode, formatHtml, type CodeLang } from '@/lib/codeHighlight';

// ============================================================
// Éditeur de code pour les thèmes : édition + aperçu coloré.
// Corrige l'affichage « texte brut » en proposant une vraie
// coloration syntaxique HTML / CSS et une réindentation propre.
// ============================================================

interface CodeEditorProps {
  value: string;
  onChange: (next: string) => void;
  language?: CodeLang;
  rows?: number;
  placeholder?: string;
}

const TOKEN_CSS = `
.code-editor-pre { font-family: 'JetBrains Mono','Fira Code',ui-monospace,monospace; font-size: 12px; line-height: 1.6; tab-size: 2; white-space: pre-wrap; word-break: break-word; }
.code-editor-pre .tok-tag { color: #c2554a; }
.code-editor-pre .tok-attr { color: #b8860b; }
.code-editor-pre .tok-string { color: #3f7d4f; }
.code-editor-pre .tok-comment { color: #8a8078; font-style: italic; }
.code-editor-pre .tok-punct { color: #6b6259; }
.code-editor-pre .tok-tagzifek { color: #7a4ea3; background: rgba(122,78,163,0.10); border-radius: 3px; padding: 0 2px; }
.code-editor-pre .tok-atrule { color: #a5543b; }
.code-editor-pre .tok-value { color: #2f5f8a; }
.code-editor-pre .tok-number { color: #2f5f8a; }
`;

export default function CodeEditor({
  value,
  onChange,
  language = 'html',
  rows = 20,
  placeholder,
}: CodeEditorProps) {
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    // Réinitialise l'aperçu quand on change de page/langage.
  }, [language]);

  const highlighted = useMemo(() => highlightCode(value || '', language), [value, language]);

  const handleFormat = () => {
    if (language === 'html') onChange(formatHtml(value || ''));
  };

  return (
    <div className="rounded-md border border-background-200/70 overflow-hidden">
      <style>{TOKEN_CSS}</style>
      <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-background-100 border-b border-background-200/70">
        <div className="flex items-center gap-0.5 bg-background-50 rounded-full p-0.5">
          <button
            onClick={() => setMode('edit')}
            className={`px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${mode === 'edit' ? 'bg-foreground-950 text-background-50' : 'text-foreground-500 hover:text-foreground-800'}`}
          >
            <i className="ri-edit-line mr-1"></i>Éditer
          </button>
          <button
            onClick={() => setMode('preview')}
            className={`px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${mode === 'preview' ? 'bg-foreground-950 text-background-50' : 'text-foreground-500 hover:text-foreground-800'}`}
          >
            <i className="ri-code-s-slash-line mr-1"></i>Code coloré
          </button>
        </div>
        <div className="flex items-center gap-2">
          {language === 'html' && (
            <button
              onClick={handleFormat}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium text-foreground-600 bg-background-50 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
              title="Réindenter le HTML"
            >
              <i className="ri-indent-increase"></i>Réindenter
            </button>
          )}
          <span className="text-[10px] text-foreground-400 font-mono bg-background-50 px-1.5 py-0.5 rounded">
            {(value || '').length} car.
          </span>
        </div>
      </div>

      {mode === 'edit' ? (
        <textarea
          ref={taRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          spellCheck={false}
          placeholder={placeholder}
          className="w-full px-3 py-2 bg-background-100 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none resize-none font-mono leading-relaxed"
          style={{ tabSize: 2 }}
        />
      ) : (
        <pre
          className="code-editor-pre px-3 py-2 bg-background-100 text-foreground-900 m-0 overflow-x-auto"
          style={{ maxHeight: `${rows * 1.6}em` }}
          dangerouslySetInnerHTML={{ __html: highlighted || '<span class="tok-comment">— vide —</span>' }}
        />
      )}
    </div>
  );
}